/**
 * Renders the rich-text trees stored inside interactive lessons.
 *
 * A value is either `{ children: Node[] }` or a bare array. Nodes seen in the
 * imported content: paragraph, text, linebreak, list, listitem — with
 * `textFormat` (bold / underline / subscript ...) and `blockFormat` (alignment).
 */
import type { CSSProperties, ReactNode } from 'react';

type Node = Record<string, any>;

const align = (blockFormat?: string): CSSProperties =>
  blockFormat && blockFormat !== 'left' ? { textAlign: blockFormat as CSSProperties['textAlign'] } : {};

function formatted(text: string, formats: string[] | undefined): ReactNode {
  let out: ReactNode = text;
  for (const f of formats ?? []) {
    if (f === 'bold') out = <strong>{out}</strong>;
    else if (f === 'italic') out = <em>{out}</em>;
    else if (f === 'underline') out = <u>{out}</u>;
    else if (f === 'strikethrough') out = <s>{out}</s>;
    else if (f === 'subscript') out = <sub>{out}</sub>;
    else if (f === 'superscript') out = <sup>{out}</sup>;
    else if (f === 'code') out = <code>{out}</code>;
  }
  return out;
}

function renderNode(node: Node, key: string): ReactNode {
  switch (node.type) {
    case 'text':
      return <span key={key}>{formatted(node.text ?? '', node.textFormat)}</span>;
    case 'linebreak':
      return <br key={key} />;
    case 'link':
      return (
        <a key={key} href={node.url ?? '#'} target="_blank" rel="noreferrer noopener">
          {children(node.children, key)}
        </a>
      );
    case 'list': {
      const Tag = node.listType === 'number' ? 'ol' : 'ul';
      return <Tag key={key} start={node.start} style={align(node.blockFormat)}>{children(node.children, key)}</Tag>;
    }
    case 'listitem':
      return <li key={key} style={align(node.blockFormat)}>{children(node.children, key)}</li>;
    case 'quote':
      return <blockquote key={key} style={align(node.blockFormat)}>{children(node.children, key)}</blockquote>;
    case 'heading': {
      const Tag = (node.tag ?? 'h3') as 'h1' | 'h2' | 'h3';
      return <Tag key={key} style={align(node.blockFormat)}>{children(node.children, key)}</Tag>;
    }
    case 'paragraph':
    default: {
      const kids = children(node.children, key);
      // Empty paragraphs are spacers in the original editor.
      if (!node.children?.length) return <p key={key} className="rt-space" />;
      return <p key={key} style={align(node.blockFormat)}>{kids}</p>;
    }
  }
}

function children(nodes: Node[] | undefined, prefix: string): ReactNode {
  return (nodes ?? []).map((n, i) => renderNode(n, `${prefix}.${i}`));
}

/** `value` may be `{children}`, an array of nodes, or a plain string. */
export function RichText({ value, className }: { value: unknown; className?: string }) {
  if (value == null) return null;
  if (typeof value === 'string') return <p className={className}>{value}</p>;
  const nodes: Node[] = Array.isArray(value) ? value : ((value as Node).children ?? []);
  return <div className={className}>{children(nodes, 'n')}</div>;
}

/** Flatten a rich-text value to plain text — for titles, alt text and summaries. */
export function plainText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  const nodes: Node[] = Array.isArray(value) ? value : ((value as Node).children ?? []);
  const out: string[] = [];
  const walk = (ns: Node[]) => {
    for (const n of ns) {
      if (n.type === 'text') out.push(n.text ?? '');
      else if (n.type === 'linebreak') out.push(' ');
      if (Array.isArray(n.children)) walk(n.children);
    }
  };
  walk(nodes);
  return out.join('').replace(/\s+/g, ' ').trim();
}
